import { lazy, type ComponentType, type LazyExoticComponent } from 'react'
import { USER_ROLES, ROUTE_PATTERNS } from '@/constants'
import { PATHROUTES } from '@/constants/path'

import CategoryManagement from '@/pages/Dashboard/CategoryManagement'
import SponsorAdManagement from '@/pages/Dashboard/SponsorAdManagement'
import UserProfile from '@/pages/Dashboard/UserProfile'
import AttendeeManagement from '@/pages/Dashboard/AttendeeManagement'
import EventDashboard from '@/pages/Dashboard/EventDashboard'
import SponsorshipManagement from '@/pages/Dashboard/Sponsorship'
import SponsorshipDetailsData from '@/components/sponsorship/SponsorshipDetailsData'
import SelectPackage from '@/pages/Dashboard/SelectPackage'
import UploadAd from '@/pages/Dashboard/UploadAd'
import MessageAdmin from '@/pages/Dashboard/MessageAdmin'
import Demographics from '@/pages/Dashboard/Demographics'
import EventAddEditForm from '@/components/event/EventAddEditForm'
import AddEditSubEvent from '@/components/sub-event/AddEditSubEvent'

const UsersManagement = lazy(() => import('@/pages/Dashboard/UsersManagment'))
const AddUser = lazy(() => import('@/pages/Dashboard/AddUser'))
const EditUser = lazy(() => import('@/pages/Dashboard/EditUser'))
const Dashboard = lazy(() => import('@/pages/Dashboard/Dashboard'))
const EventManagement = lazy(() => import('@/pages/Dashboard/EventManagement'))
const EventDetails = lazy(() => import('@/components/event/EventDetails'))

export type UserRoleType = (typeof USER_ROLES)[keyof typeof USER_ROLES]

export interface RouteConfig {
  path: string
  component:
    | ComponentType<unknown>
    | LazyExoticComponent<ComponentType<unknown>>
  roles: UserRoleType[]
  loader?: unknown
  exact?: boolean
}

export const PROTECTED_ROUTES: RouteConfig[] = [
  /* SS Admin Dashboard */
  {
    path: PATHROUTES.DASHBOARD.ROOT,
    component: Dashboard,
    roles: [USER_ROLES.superAdmin],
    exact: true,
  },

  /* User Management - (Access: SS Admin) */
  {
    path: PATHROUTES.DASHBOARD.USERS_MANAGEMENT,
    component: UsersManagement,
    roles: [USER_ROLES.superAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.ADD_USER,
    component: AddUser,
    roles: [USER_ROLES.superAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.EDIT_USER_WITH_PARAM,
    component: EditUser,
    roles: [USER_ROLES.superAdmin],
  },

  /* Event Management - (Access: SS Admin, Event Admin) */
  {
    path: PATHROUTES.DASHBOARD.EVENT_MANAGEMENT,
    component: EventManagement,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.ADD_EVENT,
    component: EventAddEditForm,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.EDIT_EVENT_WITH_PARAM,
    component: EventAddEditForm,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.EVENT_DETAILS_WITH_PARAM,
    component: EventDetails,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.ADD_SUB_EVENT_WITH_PARAM,
    component: AddEditSubEvent,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },
  {
    path: PATHROUTES.DASHBOARD.EDIT_SUB_EVENT_WITH_PARAM,
    component: AddEditSubEvent,
    roles: [USER_ROLES.superAdmin, USER_ROLES.eventAdmin],
  },

  /* Event Admin Dashboard - (Access: Event Admin) */
  {
    path: PATHROUTES.DASHBOARD.EVENT_DASHBOARD,
    component: EventDashboard,
    roles: [USER_ROLES.eventAdmin],
  },

  /* Category Management - (Access: SS Admin) */
  {
    path: PATHROUTES.DASHBOARD.CATEGORY_MANAGEMENT,
    component: CategoryManagement,
    roles: [USER_ROLES.superAdmin],
  },

  /* Attendee Management - (Access: Event Admin) */
  {
    path: PATHROUTES.DASHBOARD.ATTENDEE_MANAGEMENT,
    component: AttendeeManagement,
    roles: [USER_ROLES.eventAdmin],
  },

  /* Sponsor Demographics Dashboard - (Access: Sponsor) */
  {
    path: PATHROUTES.DASHBOARD.DEMOGRAPHICS_DASHBOARD,
    component: Demographics,
    roles: [USER_ROLES.sponsor],
  },

  /* Sponsor Ad Management - (Access: Super Admin) */
  {
    path: PATHROUTES.DASHBOARD.SPONSOR_AD_MANAGEMENT,
    component: SponsorAdManagement,
    roles: [USER_ROLES.superAdmin],
  },

  /* Sponsorship Management - (Access: Sponsor) */
  {
    path: PATHROUTES.DASHBOARD.SPONSORSHIP_MANAGEMENT,
    component: SponsorshipManagement,
    roles: [USER_ROLES.sponsor],
  },
  {
    path: PATHROUTES.DASHBOARD.SPONSORSHIP_DETAILS_WITH_PARAM,
    component: SponsorshipDetailsData,
    roles: [USER_ROLES.sponsor],
  },
  {
    path: PATHROUTES.DASHBOARD.SELECT_PACKAGE_WITH_PARAM,
    component: SelectPackage,
    roles: [USER_ROLES.sponsor],
  },
  {
    path: PATHROUTES.DASHBOARD.UPLOAD_AD_WITH_PARAM,
    component: UploadAd,
    roles: [USER_ROLES.sponsor],
  },

  /* Chat Services - (Access: SS Admin, Sponsor) */
  {
    path: PATHROUTES.DASHBOARD.MESSAGE_ADMIN,
    component: MessageAdmin,
    roles: [USER_ROLES.superAdmin, USER_ROLES.sponsor],
  },
  {
    path: PATHROUTES.DASHBOARD.MESSAGE_ADMIN_WITH_PARAM,
    component: MessageAdmin,
    roles: [USER_ROLES.superAdmin, USER_ROLES.sponsor],
  },

  /* User Profile - (Access: SS Admin, Event Admin, Sponsor, Attendee) */
  {
    path: PATHROUTES.DASHBOARD.USER_PROFILE,
    component: UserProfile,
    roles: [
      USER_ROLES.superAdmin,
      USER_ROLES.eventAdmin,
      USER_ROLES.sponsor,
      USER_ROLES.attendee,
    ],
  },
]

function matchesRoute(
  routePath: string,
  requestedPath: string,
  exact?: boolean
): boolean {
  if (exact) return routePath === requestedPath

  const pattern = routePath.replaceAll(
    ROUTE_PATTERNS.PARAM_PLACEHOLDER,
    ROUTE_PATTERNS.PARAM_MATCHER
  )
  return new RegExp(`^${pattern}$`).test(requestedPath)
}

export function hasPermission(
  userRole: string | undefined,
  requestedPath: string
): boolean {
  if (!userRole) return false

  const route = PROTECTED_ROUTES.find(r =>
    matchesRoute(r.path, requestedPath, r.exact)
  )

  return route ? route.roles.includes(userRole as UserRoleType) : false
}
